import classNames from 'classnames'
import { useI18n } from 'i18n-utils'
import { PLATFORM_CONFIG } from 'platform-config'
import { useUserInfoStore } from 'store'
import { omit, transformHexToRgba } from 'utils'
import type { HTMLAttributes } from 'vue'
import { UiUtils } from '..'
import { LoadingView } from './LoadingView'
import './Button.scss'

type ButtonSize = 'small' | 'medium' | 'large'

const sizeConfig: Record<ButtonSize, { height: number; fontSize: number; padding: string }> = {
  small: { height: 32, fontSize: 14, padding: '0 15px' },
  medium: { height: 40, fontSize: 15, padding: '0 20px' },
  large: { height: 44, fontSize: 16, padding: '0 24px' },
}

interface CustomButtonProps extends HTMLAttributes {
  type?: 'primary' | 'default' | 'danger' | 'aux'
  size?: ButtonSize
  loading?: boolean
  disabled?: boolean
  radius?: number
  primaryColor?: string
  isLoginClick?: boolean
  isFull?: boolean
}

export const CustomButton = defineComponent<CustomButtonProps>({
  name: 'CustomButton',
  props: {
    type: {
      type: String,
      default: 'default',
    },
    size: {
      type: String as PropType<ButtonSize>,
      default: 'small',
    },
    isFull: {
      type: Boolean,
      default: false,
    },
    loading: {
      type: Boolean,
      default: false,
    },
    disabled: {
      type: Boolean,
      default: false,
    },
    onClick: {
      type: Function as PropType<HTMLAttributes['onClick']>,
      default: () => {},
    },
    radius: {
      type: Number,
      default: 8,
    },
    primaryColor: {
      type: String,
      default: () => PLATFORM_CONFIG.colors.primaryGradient || PLATFORM_CONFIG.colors.primary,
    },
    isLoginClick: {
      type: Boolean,
      default: false,
    },
  },

  setup(props, { slots }) {
    const userInfoStore = useUserInfoStore()
    const { t } = useI18n()
    return () => {
      const size = props.size || 'small'
      const cfg = sizeConfig[size]
      const primaryColor = props.primaryColor!
      return (
        <div
          {...omit(props, ['onClick'])}
          role="button"
          style={{
            '--btn-padding': cfg.padding,
            '--btn-height': `${cfg.height}px`,
            '--btn-font-size': `${cfg.fontSize}px`,
            '--btn-radius': `${props.radius || 8}px`,
            '--btn-width': props.isFull ? '100%' : 'auto',
            '--btn-primary-color': primaryColor,
            '--btn-primary-color-hover': transformHexToRgba(primaryColor, 0.8),
          }}
          class={classNames(`custom-button custom-button__${props.type}`, {
            'custom-button__disabled': props.disabled,
          })}
          onClick={(e) => {
            e.stopPropagation()
            if (props.isLoginClick && !userInfoStore.isLogin) {
              UiUtils.showWarning(t('请先登录'))
              UiUtils.gotoLogin?.()
              return
            }
            if (props.loading || props.disabled) {
              return
            }
            props.onClick?.(e)
          }}
        >
          {props.loading ? <LoadingView class={'loading-btn'} /> : slots.default?.()}
        </div>
      )
    }
  },
})

export { CustomButton as ButtonLayout }
